import React, { useState, useEffect } from 'react';
import {
    Users,
    MapPin,
    Building2,
    Home,
    Wallet,
    Bed,
    RefreshCw,
    UserPlus,
    Calendar,
    PawPrint,
    Car,
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie
} from 'recharts';
import { fetchRentingPreferencesAnalytics } from '../api/analyticsApi';
import RenterPreferenceMatchesModal from './RenterPreferenceMatchesModal';
import toast from 'react-hot-toast';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

/**
 * Renter Analytics — the renter counterpart of BuyerAnalytics.js (Monday
 * 12908991007, Christian Gravias, 2026-08-27: "Create Renter Analytic,
 * similar to buyer analytics").
 *
 * Trimmed to the five dimensions RentingPreference actually has (suburbs,
 * cities, property type, bedrooms, weekly rent) - there's no renter
 * equivalent of buyer_type, finance_status, land_size or buying_timeline, so
 * those cards (and the "Deactivated Accounts" tile, which only makes sense
 * for portal accounts - most renters here have none) are left out rather than
 * shown empty. The Suburb Preferences card doubles as the "priority suburbs"
 * view (already ranked by demand), standing in for a separate Targeting
 * Brief-style panel.
 */
const RenterAnalytics = ({ onRenterClick, startDate, endDate }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [drilldown, setDrilldown] = useState({ open: false, filterType: null, filterValue: null });
    const [showAllSuburbs, setShowAllSuburbs] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const result = await fetchRentingPreferencesAnalytics(startDate, endDate);
                if (!cancelled) setData(result);
            } catch (error) {
                console.error('Error loading renter preferences analytics:', error);
                if (!cancelled) toast.error('Failed to load renter preferences');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    const openDrilldown = (filterType, filterValue) => {
        if (filterValue == null || filterValue === '') return;
        setDrilldown({ open: true, filterType, filterValue });
    };

    const closeDrilldown = () => setDrilldown((d) => ({ ...d, open: false }));

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    if (!data) return null;

    const PreferenceCard = ({ title, icon: Icon, children, className = "", headerAction = null }) => (
        <div className={`bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden ${className}`}>
            <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-gray-500" />
                    <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wider">{title}</h3>
                </div>
                {headerAction}
            </div>
            <div className="p-4">
                {children}
            </div>
        </div>
    );

    const ListPreference = ({ items, filterType, labelKey = 'name', countKey = 'count', maxItems = 8 }) => (
        <div className={`space-y-3 ${maxItems > 8 ? 'max-h-80 overflow-y-auto pr-1' : ''}`}>
            {items.slice(0, maxItems).map((item, i) => (
                <button
                    key={i}
                    type="button"
                    onClick={() => openDrilldown(filterType, item[labelKey])}
                    className="w-full flex items-center justify-between text-left rounded-md -mx-1 px-1 py-1 hover:bg-blue-50 transition-colors cursor-pointer"
                    title={`Show renters wanting ${item[labelKey]}`}
                >
                    <span className="text-sm font-medium text-gray-700 truncate mr-4 hover:text-blue-700">{item[labelKey]}</span>
                    <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-100 h-1.5 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-blue-500"
                                style={{ width: `${(item[countKey] / data.total_renters) * 100}%` }}
                            />
                        </div>
                        <span className="text-xs font-bold text-gray-500 w-8 text-right">{item[countKey]}</span>
                    </div>
                </button>
            ))}
        </div>
    );

    const preferenceCharts = (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <PreferenceCard title="City Preferences" icon={Building2} className="lg:col-span-1">
                {(data.cities && data.cities.length > 0) ? (
                    <ListPreference items={data.cities} filterType="city" />
                ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No city preferences</p>
                )}
            </PreferenceCard>

            <PreferenceCard
                title="Suburb Preferences"
                icon={MapPin}
                className="lg:col-span-1"
                headerAction={data.suburbs && data.suburbs.length > 8 ? (
                    <button
                        type="button"
                        onClick={() => setShowAllSuburbs((v) => !v)}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                    >
                        {showAllSuburbs ? 'Show top 8' : `Show all (${data.suburbs.length})`}
                    </button>
                ) : null}
            >
                {(data.suburbs && data.suburbs.length > 0) ? (
                    <ListPreference
                        items={data.suburbs}
                        filterType="suburb"
                        maxItems={showAllSuburbs ? data.suburbs.length : 8}
                    />
                ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No suburb preferences</p>
                )}
            </PreferenceCard>

            <PreferenceCard title="Property Type" icon={Home} className="lg:col-span-1">
                {(data.property_types && data.property_types.length > 0) ? (
                    <>
                        <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                                <Pie
                                    data={data.property_types}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="count"
                                    onClick={(entry) => openDrilldown('property_type', entry?.name)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {data.property_types.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="flex flex-wrap gap-4 justify-center mt-2">
                            {data.property_types.map((entry, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => openDrilldown('property_type', entry.name)}
                                    className="flex items-center gap-1.5 hover:bg-gray-50 rounded px-1 py-0.5 transition-colors cursor-pointer"
                                >
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                                    <span className="text-xs font-medium text-gray-600 hover:text-blue-700">{entry.name} ({entry.count})</span>
                                </button>
                            ))}
                        </div>
                    </>
                ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No property type preferences</p>
                )}
            </PreferenceCard>

            <PreferenceCard title="Max. Bedrooms" icon={Bed} className="lg:col-span-1">
                {(data.bedrooms && data.bedrooms.length > 0) ? (
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={data.bedrooms}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" fontSize={12} />
                            <YAxis fontSize={12} />
                            <Tooltip />
                            <Bar
                                dataKey="count"
                                fill="#3B82F6"
                                radius={[4, 4, 0, 0]}
                                onClick={(entry) => openDrilldown('bedrooms', entry?.name)}
                                style={{ cursor: 'pointer' }}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No bedroom preferences</p>
                )}
            </PreferenceCard>

            <PreferenceCard title="Weekly Rent (Max)" icon={Wallet} className="lg:col-span-2">
                {(data.rent_ranges && data.rent_ranges.length > 0) ? (
                    <ListPreference items={data.rent_ranges} filterType="rent_max" />
                ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No stated rent budgets yet</p>
                )}
            </PreferenceCard>

            <PreferenceCard title="Move-in Timeframe" icon={Calendar} className="lg:col-span-1">
                {(data.move_in_timeframes && data.move_in_timeframes.length > 0) ? (
                    <ListPreference items={data.move_in_timeframes} filterType="move_in_timeframe" />
                ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No move-in timeframes yet</p>
                )}
            </PreferenceCard>

            <PreferenceCard title="Pets" icon={PawPrint} className="lg:col-span-1">
                {(data.pets && data.pets.length > 0) ? (
                    <ListPreference items={data.pets} filterType="pets" />
                ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No pet preferences yet</p>
                )}
            </PreferenceCard>

            <PreferenceCard title="Occupants" icon={Users} className="lg:col-span-1">
                {(data.occupants && data.occupants.length > 0) ? (
                    <ListPreference items={data.occupants} filterType="occupants" />
                ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No occupant counts yet</p>
                )}
            </PreferenceCard>

            <PreferenceCard title="Car Spaces" icon={Car} className="lg:col-span-1">
                {(data.car_spaces && data.car_spaces.length > 0) ? (
                    <ListPreference items={data.car_spaces} filterType="car_spaces" />
                ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No car space preferences yet</p>
                )}
            </PreferenceCard>

            <PreferenceCard title="Secondary Suburbs" icon={MapPin} className="lg:col-span-2">
                {(data.secondary_suburbs && data.secondary_suburbs.length > 0) ? (
                    <ListPreference items={data.secondary_suburbs} filterType="secondary_suburb" />
                ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No secondary suburbs yet</p>
                )}
            </PreferenceCard>
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <button
                    type="button"
                    onClick={() => openDrilldown('all', 'all')}
                    title="View all renters"
                    className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4 text-left w-full transition-all hover:border-blue-200 hover:shadow-md cursor-pointer"
                >
                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
                        <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Total Renters</p>
                        <p className="text-2xl font-bold text-gray-900">{data.total_renters}</p>
                        <p className="text-[11px] text-blue-600 font-medium mt-0.5">Click to view list</p>
                    </div>
                </button>

                {[
                    { label: 'Added last 7 days', value: data.added_last_7 },
                    { label: 'Added last 30 days', value: data.added_last_30 },
                    { label: 'Added last 90 days', value: data.added_last_90 },
                ].map((tile) => (
                    <div
                        key={tile.label}
                        className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4"
                    >
                        <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                            <UserPlus className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500">{tile.label}</p>
                            <p className="text-2xl font-bold text-gray-900">{tile.value ?? 0}</p>
                        </div>
                    </div>
                ))}
            </div>

            {preferenceCharts}

            <RenterPreferenceMatchesModal
                isOpen={drilldown.open}
                onClose={closeDrilldown}
                filterType={drilldown.filterType}
                filterValue={drilldown.filterValue}
                startDate={startDate}
                endDate={endDate}
                onRenterClick={onRenterClick}
            />
        </div>
    );
};

export default RenterAnalytics;
